import { useMemo, useState } from "react";
import {
  ActionIcon,
  Badge,
  Button,
  Container,
  Grid,
  Group,
  Loader,
  Menu,
  Modal,
  NumberInput,
  Paper,
  ScrollArea,
  Select,
  Stack,
  Switch,
  Table,
  Text,
  TextInput,
  Textarea,
  ThemeIcon,
  Title,
} from "@mantine/core";
import { useForm } from "@mantine/form";
import { motion } from "framer-motion";
import {
  IconAdjustmentsDollar,
  IconCheck,
  IconDots,
  IconPercentage,
  IconPlus,
  IconTrash,
  IconWallet,
} from "@tabler/icons-react";
import { usePerformRequest, PerformRequest } from "../../lib/usePerformRequest";
import { Endpoints } from "../../lib/endpoints";
import { HttpCodes } from "../../lib/data";
import { NonPaginatedResponse } from "../../types";
import { IFeeListResponse, IFeeProps, FeeRateType, FeeStatus } from "../../types/Fee";
import { useToastNotification } from "../../hooks/useToastNotification";

const rateTypeOptions: { label: string; value: FeeRateType }[] = [
  { label: "Percentage", value: "percentage" },
  { label: "Flat", value: "flat" },
];

const frequencyOptions: { label: string; value: string }[] = [
  { label: "Once", value: "once" },
  { label: "Monthly", value: "monthly" },
  { label: "Annually", value: "annually" },
  { label: "Per Transaction", value: "per_transaction" },
  { label: "Per Loan", value: "per_loan" },
];

const applicableToOptions: { label: string; value: string }[] = [
  { label: "Principal", value: "principal" },
  { label: "Amount Due", value: "amount_due" },
  { label: "Balance Outstanding", value: "balance_outstanding" },
  { label: "Total Amount", value: "total_amount" },
];

const statusOptions: { label: string; value: FeeStatus }[] = [
  { label: "Active", value: "active" },
  { label: "Inactive", value: "inactive" },
];

const formatCurrency = (value: number) =>
  new Intl.NumberFormat("en-NG", { style: "currency", currency: "NGN", maximumFractionDigits: 0 }).format(value);

const formatValue = (fee: IFeeProps) =>
  fee.rateType === "percentage" ? `${fee.value}%` : formatCurrency(fee.value);

const statusColor = (status: FeeStatus) => (status === "active" ? "green" : "gray");

type FormValues = {
  name: string;
  description: string;
  rateType: FeeRateType;
  value: number;
  frequency: string;
  applicableTo: string;
  status: FeeStatus;
};

export default function Fees() {
  const { data, isLoading, reloadData } = usePerformRequest<NonPaginatedResponse<IFeeListResponse>>({
    method: Endpoints.GetFees().method,
    route: Endpoints.GetFees().route,
  });
  const { success, error } = useToastNotification();

  const fees = data?.data?.items ?? [];
  const stats = data?.data?.stats;

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [editingFee, setEditingFee] = useState<IFeeProps | null>(null);
  const [feeToDelete, setFeeToDelete] = useState<IFeeProps | null>(null);

  const form = useForm<FormValues>({
    initialValues: {
      name: "",
      description: "",
      rateType: "percentage",
      value: 0,
      frequency: "once",
      applicableTo: "principal",
      status: "active",
    },
    validate: {
      name: (value) => (value.trim().length === 0 ? "Name is required" : null),
      value: (value) => (value < 0 ? "Value cannot be negative" : null),
      frequency: (value) => (value.trim().length === 0 ? "Frequency is required" : null),
      applicableTo: (value) => (value.trim().length === 0 ? "Applicable To is required" : null),
    },
  });

  const resetForm = () => {
    form.setValues({
      name: "",
      description: "",
      rateType: "percentage",
      value: 0,
      frequency: "once",
      applicableTo: "principal",
      status: "active",
    });
    form.resetDirty();
  };

  const handleOpenCreate = () => {
    setEditingFee(null);
    resetForm();
    setIsModalOpen(true);
  };

  const handleOpenEdit = (fee: IFeeProps) => {
    setEditingFee(fee);
    form.setValues({
      name: fee.name,
      description: fee.description ?? "",
      rateType: fee.rateType,
      value: fee.value,
      frequency: fee.frequency,
      applicableTo: fee.applicableTo,
      status: fee.status,
    });
    form.resetDirty();
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingFee(null);
    resetForm();
  };

  const handleSubmit = form.onSubmit(async (values) => {
    setIsSubmitting(true);
    try {
      const endpoint = editingFee ? Endpoints.UpdateFee(editingFee.id) : Endpoints.CreateFee();
      const response = await PerformRequest({
        method: endpoint.method,
        route: endpoint.route,
        data: {
          ...values,
          name: values.name.trim(),
          description: values.description?.trim() || undefined,
          frequency: values.frequency.trim(),
          applicableTo: values.applicableTo.trim(),
        },
      });

      const successStatus = editingFee ? HttpCodes.Success : HttpCodes.Created;
      if (response.status === successStatus) {
        success(`Fee ${editingFee ? "updated" : "created"} successfully`);
        await reloadData();
        handleCloseModal();
      } else {
        error(response.message || `Failed to ${editingFee ? "update" : "create"} fee`);
      }
    } catch (err: any) {
      error(err.message || `Failed to ${editingFee ? "update" : "create"} fee`);
    } finally {
      setIsSubmitting(false);
    }
  });

  const handleDelete = async () => {
    if (!feeToDelete) return;
    setIsDeleting(true);
    try {
      const response = await PerformRequest({
        method: Endpoints.DeleteFee(feeToDelete.id).method,
        route: Endpoints.DeleteFee(feeToDelete.id).route,
      });

      if (response.status === HttpCodes.Success) {
        success(`Fee "${feeToDelete.name}" deleted successfully`);
        await reloadData();
        setFeeToDelete(null);
      } else {
        error(response.message || "Failed to delete fee");
      }
    } catch (err: any) {
      error(err.message || "Failed to delete fee");
    } finally {
      setIsDeleting(false);
    }
  };

  const sortedFees = useMemo(() => [...fees].sort((a, b) => b.createdAt - a.createdAt), [fees]);

  return (
    <Container size="xl" py="xl">
      <Stack gap="xl">
        <motion.div initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
          <Group justify="space-between" align="flex-end" mb="md">
            <div>
              <Title order={1} size="3rem" fw={800} style={{ color: "#679369", marginBottom: "0.5rem" }}>
                Fees Management
              </Title>
              <Text size="lg" c="dimmed">
                View and manage loan fees and charges
              </Text>
            </div>
            <Button leftSection={<IconPlus size={18} />} onClick={handleOpenCreate} color="green" variant="filled">
              Create Fee
            </Button>
          </Group>

          <Grid gutter="md" mb="lg">
            <Grid.Col span={{ base: 12, sm: 4 }}>
              <Paper
                p="md"
                radius="md"
                style={{ background: "linear-gradient(135deg, #E0F2FE 0%, #BAE6FD 100%)", border: "1px solid #0EA5E9" }}
              >
                <Group>
                  <ThemeIcon size="lg" radius="sm" style={{ background: "#0EA5E9" }}>
                    <IconAdjustmentsDollar size={20} />
                  </ThemeIcon>
                  <div>
                    <Text size="xs" fw={500} c="#0C4A6E">
                      Total Fees
                    </Text>
                    <Text size="xl" fw={700} c="#0C4A6E">
                      {stats?.total ?? 0}
                    </Text>
                  </div>
                </Group>
              </Paper>
            </Grid.Col>
            <Grid.Col span={{ base: 12, sm: 4 }}>
              <Paper
                p="md"
                radius="md"
                style={{ background: "linear-gradient(135deg, #DCFCE7 0%, #BBF7D0 100%)", border: "1px solid #22C55E" }}
              >
                <Group>
                  <ThemeIcon size="lg" radius="sm" style={{ background: "#22C55E" }}>
                    <IconCheck size={20} />
                  </ThemeIcon>
                  <div>
                    <Text size="xs" fw={500} c="#166534">
                      Active Fees
                    </Text>
                    <Text size="xl" fw={700} c="#166534">
                      {stats?.active ?? 0}
                    </Text>
                  </div>
                </Group>
              </Paper>
            </Grid.Col>
            <Grid.Col span={{ base: 12, sm: 4 }}>
              <Paper
                p="md"
                radius="md"
                style={{ background: "linear-gradient(135deg, #F3F4F6 0%, #E5E7EB 100%)", border: "1px solid #9CA3AF" }}
              >
                <Group>
                  <ThemeIcon size="lg" radius="sm" style={{ background: "#9CA3AF" }}>
                    <IconWallet size={20} />
                  </ThemeIcon>
                  <div>
                    <Text size="xs" fw={500} c="#4B5563">
                      Inactive Fees
                    </Text>
                    <Text size="xl" fw={700} c="#4B5563">
                      {stats?.inactive ?? 0}
                    </Text>
                  </div>
                </Group>
              </Paper>
            </Grid.Col>
          </Grid>
        </motion.div>

        <Paper shadow="sm" radius="md" withBorder>
          <ScrollArea>
            {isLoading ? (
              <Stack align="center" gap="sm" py="xl">
                <Loader color="green" />
                <Text size="sm" c="dimmed">
                  Loading fees...
                </Text>
              </Stack>
            ) : sortedFees.length === 0 ? (
              <Stack align="center" gap="sm" py="xl">
                <ThemeIcon size={48} radius="xl" variant="light" color="gray">
                  <IconAdjustmentsDollar size={28} />
                </ThemeIcon>
                <Text fw={600} size="lg">
                  No fees configured yet
                </Text>
                <Text size="sm" c="dimmed" ta="center" maw={320}>
                  Create your first fee to start tracking loan charges.
                </Text>
                <Button leftSection={<IconPlus size={18} />} onClick={handleOpenCreate}>
                  Create Fee
                </Button>
              </Stack>
            ) : (
              <Table highlightOnHover striped>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>Fee</Table.Th>
                    <Table.Th>Frequency</Table.Th>
                    <Table.Th>Applicable To</Table.Th>
                    <Table.Th>Rate</Table.Th>
                    <Table.Th>Status</Table.Th>
                    <Table.Th>Actions</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {sortedFees.map((fee) => (
                    <Table.Tr key={fee.id}>
                      <Table.Td>
                        <Stack gap={4}>
                          <Group gap="xs">
                            <ThemeIcon size="sm" radius="sm" variant="light" color={fee.rateType === "percentage" ? "blue" : "orange"}>
                              {fee.rateType === "percentage" ? <IconPercentage size={14} /> : <IconAdjustmentsDollar size={14} />}
                            </ThemeIcon>
                            <Text fw={600}>{fee.name}</Text>
                          </Group>
                          <Text size="xs" c="dimmed">
                            {fee.description || "No description provided"}
                          </Text>
                        </Stack>
                      </Table.Td>
                      <Table.Td>
                        <Text size="sm">{fee.frequency}</Text>
                      </Table.Td>
                      <Table.Td>
                        <Badge color="green" variant="light">
                          {fee.applicableTo}
                        </Badge>
                      </Table.Td>
                      <Table.Td>
                        <Text size="sm" fw={600}>
                          {formatValue(fee)}
                        </Text>
                      </Table.Td>
                      <Table.Td>
                        <Badge color={statusColor(fee.status)} variant="filled">
                          {fee.status === "active" ? "Active" : "Inactive"}
                        </Badge>
                      </Table.Td>
                      <Table.Td width={80}>
                        <Menu withinPortal position="bottom-end" shadow="sm">
                          <Menu.Target>
                            <ActionIcon variant="subtle" color="gray">
                              <IconDots size={18} />
                            </ActionIcon>
                          </Menu.Target>
                          <Menu.Dropdown>
                            <Menu.Item leftSection={<IconAdjustmentsDollar size={16} />} onClick={() => handleOpenEdit(fee)}>
                              Edit
                            </Menu.Item>
                            <Menu.Item
                              color="red"
                              leftSection={<IconTrash size={16} />}
                              onClick={() => setFeeToDelete(fee)}
                            >
                              Delete
                            </Menu.Item>
                          </Menu.Dropdown>
                        </Menu>
                      </Table.Td>
                    </Table.Tr>
                  ))}
                </Table.Tbody>
              </Table>
            )}
          </ScrollArea>
        </Paper>
      </Stack>

      <Modal opened={isModalOpen} onClose={handleCloseModal} title={editingFee ? "Edit Fee" : "Create Fee"} size="lg">
        <form onSubmit={handleSubmit}>
          <Stack gap="md">
            <TextInput label="Fee Name" placeholder="e.g. Management Fee" required {...form.getInputProps("name")} />
            <Textarea
              label="Description"
              placeholder="Optional description"
              minRows={2}
              autosize
              {...form.getInputProps("description")}
            />

            <Grid>
              <Grid.Col span={{ base: 12, sm: 6 }}>
                <Select
                  label="Rate Type"
                  data={rateTypeOptions}
                  value={form.values.rateType}
                  onChange={(value) => form.setFieldValue("rateType", (value as FeeRateType) || "percentage")}
                />
              </Grid.Col>
              <Grid.Col span={{ base: 12, sm: 6 }}>
                <NumberInput
                  label={form.values.rateType === "percentage" ? "Value (%)" : "Value (₦)"}
                  min={0}
                  value={form.values.value}
                  onChange={(value) => form.setFieldValue("value", Number(value) || 0)}
                  error={form.errors.value}
                />
              </Grid.Col>
            </Grid>

            <Select
              label="Frequency"
              data={frequencyOptions}
              value={form.values.frequency}
              onChange={(value) => form.setFieldValue("frequency", value || "once")}
              required
              error={form.errors.frequency}
            />
            <Select
              label="Applicable To"
              data={applicableToOptions}
              value={form.values.applicableTo}
              onChange={(value) => form.setFieldValue("applicableTo", value || "principal")}
              required
              error={form.errors.applicableTo}
            />

            <Switch
              label="Active"
              checked={form.values.status === "active"}
              onChange={(event) => form.setFieldValue("status", event.currentTarget.checked ? "active" : "inactive")}
            />

            <Group justify="flex-end" mt="md">
              <Button variant="outline" onClick={handleCloseModal} disabled={isSubmitting}>
                Cancel
              </Button>
              <Button type="submit" loading={isSubmitting}>
                {editingFee ? "Update" : "Create"}
              </Button>
            </Group>
          </Stack>
        </form>
      </Modal>

      <Modal opened={Boolean(feeToDelete)} onClose={() => setFeeToDelete(null)} title="Delete Fee" centered>
        <Stack gap="md">
          <Text>
            Are you sure you want to delete <strong>{feeToDelete?.name}</strong>? This action cannot be undone.
          </Text>
          <Group justify="flex-end">
            <Button variant="outline" onClick={() => setFeeToDelete(null)} disabled={isDeleting}>
              Cancel
            </Button>
            <Button color="red" onClick={handleDelete} loading={isDeleting} leftSection={<IconTrash size={16} />}>
              Delete
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Container>
  );
}

